import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router";

import EditButton from "../../../components/buttons/edit-button/EditButton";
import DisplayDateCreated from "../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../components/data-components/DisplayCreatedBy";
import {
  DisplaySewerObjectId,
  DisplayDataOwner,
  DisplayInProject,
  DisplayOpenJobs,
} from "../components/DataComponents";
import {
  ActionsSection,
  ButtonsSection,
  SubSection,
  DataSection,
} from "../../../components/data-components/sections/DataSections";
import { DisplayDynamicProperties } from "../../../components/data-components/DisplayDynamicProperties";
import DeleteButton from "../../../components/buttons/delete-button/DeleteButton";
import { SectionMenuItemsModel } from "./SectionMenuItems";
import {
  ManageDeletedData,
  ViewSewerObject as ViewSectionPermission,
  ViewSewerObjectFromAssignedProject,
} from "../../../services/authorization/Permissions";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { snackbarNotificationsService } from "../../../services/notifications/SnackbarNotificationsService";
import useUrlParams from "../../../services/url/UrlParamsService";
import { CurrentSectionStorageModel } from "../../../services/sewer-object/CurrentSectionStorage";
import { api } from "../../../services/api/ApiService";
import PageLayout from "../../_layout/PageLayout";
import GenericObjectIssues from "../../../components/generic-object-issues/GenericObjectIssues";
import { CollapsibleServiceModel } from "../../../services/collapsible/CollapsibleService";
import { appSessionStorage } from "../../../services/storage/SessionStorageService";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { navigatePath } from "../../../helpers/StringHelpers";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";
import useRouteService from "../../../services/route-service/RouteService";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();
  const { isUserOnProjects } = useRouteService();

  const [currentSectionStorage] = useState(() =>
    CurrentSectionStorageModel.create(
      {
        currentSectionId: urlParamsService.getSectionId()!,
        parentEntityFromPath: {
          id: urlParamsService.getSewerObjectParentId()!,
          type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job,
        },
      },
      { api }
    )
  );

  const collapsibleService = CollapsibleServiceModel.create(
    { collapsedContentKey: "genericIssuesSectionIsCollapsed" },
    { appSessionStorage }
  );

  const menuItems = useMemo(() => {
    const sectionMenuItems = SectionMenuItemsModel.create({
      sectionId: currentSectionStorage.currentSectionId,
      section: currentSectionStorage.currentSection,
      parentIdFromPath: currentSectionStorage.parentEntityFromPath.id,
      shouldDisableMenuItems: currentSectionStorage.isDeleteInProgress,
    });

    return {
      staticItems: sectionMenuItems.getStaticMenuItems(),
      dynamicItems: sectionMenuItems.getDynamicMenuItems(),
    };
  }, [
    currentSectionStorage.currentSectionId,
    currentSectionStorage.currentSection,
    currentSectionStorage.parentEntityFromPath.id,
    currentSectionStorage.isDeleteInProgress,
  ]);

  const getSectionIsLockedWarning = useCallback(() => {
    if (
      currentSectionStorage.userWithLockOnSection &&
      !currentSectionStorage.currentUserHasLockOnSection
    )
      return {
        key: "ViewSection.locked-inspection",
        context: { userName: currentSectionStorage.userWithLockOnSection.name },
      };
    else return undefined;
  }, [
    currentSectionStorage.userWithLockOnSection,
    currentSectionStorage.currentUserHasLockOnSection,
  ]);

  const handleEditClick = () => {
    history.push(`${history.location.pathname}/edit`);
  };

  const handleDeleteSection = async () => {
    try {
      await currentSectionStorage.deleteSection();
      history.push(`${navigatePath(history.location.pathname, -2)}`);
      snackbarNotificationsService.showSuccess("ViewSection.delete.success");
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error.section.delete");
      handleErrorInSnackbar(applicationError);
    }
  };

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([{
        entity: currentSectionStorage.parentEntityFromPath.type,
        name: await currentSectionStorage.getParentEntityName(currentSectionStorage.currentSection.project.name)
        }, {
        entity: EntityTypeFromPath.SewerObject,
        name: currentSectionStorage.currentSection.name
        }
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [currentSectionStorage, currentSectionStorage.currentSection.name, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewSectionPermission, ViewSewerObjectFromAssignedProject]}
      titleKey="ViewSection.title"
      titleContext={{ sectionName: currentSectionStorage.currentSection.name }}
      id="view-section"
      menuItems={menuItems}
      isLoading={currentSectionStorage.isLoading}
    >
      <ActionsSection
        warningKey={getSectionIsLockedWarning()?.key}
        warningContext={getSectionIsLockedWarning()?.context}
      >
        <ButtonsSection>
          <EditButton
            disabled={!currentSectionStorage.canUserEditSection}
            onClick={handleEditClick}
          />
          <DeleteButton
            disabled={!currentSectionStorage.canUserDeleteSection}
            confirmationAction={handleDeleteSection}
            confirmationModalTitleKey="ViewSection.delete"
            confirmationModalSubtitleKey="ViewSection.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
        </ButtonsSection>
      </ActionsSection>
      <GenericObjectIssues
        collapsibleService={collapsibleService}
        genericIssues={currentSectionStorage.currentSection.genericIssues}
      />
      <DataSection labelKey="general.section.details">
        <SubSection>
          <DisplaySewerObjectId
            id={currentSectionStorage.currentSection.name}
            labelKey="sewer-objects.DisplaySectionId.label"
          />
          <DisplayDataOwner dataOwner={currentSectionStorage.currentSection.dataOwner} />
        </SubSection>
        <SubSection>
          <DisplayInProject project={currentSectionStorage.currentSection.project} enableRedirect />
          <DisplayOpenJobs openJobs={currentSectionStorage.currentSection.openJobs} />
        </SubSection>
        <SubSection>
          <DisplayDateCreated date={currentSectionStorage.currentSection.created} />
          <DisplayCreatedBy createdBy={currentSectionStorage.currentSection.createdBy.name} />
        </SubSection>
      </DataSection>
      <DisplayDynamicProperties
        labelKey="general.characteristics"
        properties={currentSectionStorage.currentSection.getDynamicPropertiesForView()}
      />
    </PageLayout>
  );
});
